import { Check, X } from 'lucide-react-native';
import { Pressable } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import type { FunnelStage } from '../../../types/leads';

interface StageMoveModalProps {
  visible: boolean;
  onClose: () => void;
  stages: FunnelStage[];
  currentStageId: string;
  onSelectStage: (stageId: string) => void;
  loading?: boolean;
}

export function StageMoveModal({
  visible,
  onClose,
  stages,
  currentStageId,
  onSelectStage,
  loading,
}: Readonly<StageMoveModalProps>) {
  const { t } = useTranslation();
  const translateStageName = useTranslatedStageName();

  return (
    <Sheet visible={visible} onClose={onClose} position="center" size="sm" hideClose>
      <Box testID="stage_move_modal">
        <HStack justify="between" align="center" style={{ marginBottom: 16 }}>
          <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
            {t('leads.stageMove.title')}
          </Text>
          <Pressable testID="stage_move_close_button" onPress={onClose}>
            <X size={20} color={colors.text.tertiary} />
          </Pressable>
        </HStack>

        <VStack spacing="xs">
          {stages.map((stage) => {
            const isCurrent = stage.id === currentStageId;
            return (
              <Pressable
                key={stage.id}
                testID={`stage_move_option_${stage.id}`}
                onPress={() => !isCurrent && onSelectStage(stage.id)}
                disabled={isCurrent || loading}
              >
                <Box
                  backgroundColor={isCurrent ? colors.primary.muted : colors.background.secondary}
                  borderRadius="md"
                  borderWidth={1}
                  borderColor={
                    isCurrent ? withAlpha(colors.primary[500], 0.25) : colors.border.muted
                  }
                  padding="md"
                  opacity={loading ? 0.6 : 1}
                >
                  <HStack spacing="md" align="center">
                    <Box width={12} height={12} borderRadius="full" backgroundColor={stage.color} />
                    <Text
                      variant="body"
                      color={isCurrent ? colors.primary[500] : colors.text.primary}
                      style={{ fontWeight: isCurrent ? '600' : '400', flex: 1 }}
                    >
                      {translateStageName(stage.name)}
                    </Text>
                    {isCurrent && <Check size={16} color={colors.primary[500]} strokeWidth={2} />}
                  </HStack>
                </Box>
              </Pressable>
            );
          })}
        </VStack>

        <Box style={{ marginTop: 16 }}>
          <Button
            testID="stage_move_cancel_button"
            variant="ghost"
            size="sm"
            onPress={onClose}
            fullWidth
          >
            {t('common.actions.cancel')}
          </Button>
        </Box>
      </Box>
    </Sheet>
  );
}
